import { StarOutlineOutlined as StarOutlineOutlinedIcon } from 'components/icons';
import MatterIndicators from 'components/matters/MatterIndicators';
import StatusChip from 'components/@extended/StatusChip.tsx';
import { buttonStyle } from 'components/buttons/buttonStyle';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetExperienceRecordsWithFiltersQuery } from 'store/api/experience-records';
import { useStartMatterExperienceRecordExportMutation } from 'store/api/exports.ts';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups.ts';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import {
    useBulkUpdateMatterExperienceRecordExclusionMutation,
    useUpdateMatterExperienceRecordExclusionMutation,
} from 'store/api/matters.ts';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import {
    useLazyGetPracticeAreasWithFiltersQuery,
    useLazyGetSecondLevelPracticeAreasUngroupedQuery,
} from 'store/api/practice-areas.ts';
import { useGetHasSectorGroupsQuery, useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { PartyType, RelationshipTypeCode } from 'types/contacts.ts';
import { MatterExperienceRecordDto } from 'types/experience-records.ts';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features.ts';
import { PracticeAreaType } from 'types/practice-areas.ts';
import { TeamType } from 'types/teams.ts';
import {
    enrichDynamicFiltersWithPassiveFilter,
    filterMainFilters,
    filterQuickFilters,
    PaginatedFieldPassiveFilter,
} from 'utils/filterUtil.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { partyNameString } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState.ts';
import { filterMatterStateTypes } from '../matters/MattersTableWithFilters.tsx';
import { getLinks, getStatusChipType } from '../matters/util.tsx';
import ExperienceRecordActionButtons from './components/ExperienceRecordActionButtons.tsx';
import { AssignedEmployeesCell } from './components/AssignedEmployeesCell.tsx';
import { OpenedDateCell } from './components/OpenedDateCell.tsx';
import { XrConfidentialMarker } from './components/XrConfidentialMarker';
import { XrDraftChip } from './components/XrDraftChip';

const ENTITY_LINK = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

type FilterId = 'isMyCaseQF' | 'hasExperienceRecordQF' | 'includedToXRQF' | 'hideDraftsQF';
type FilterGroup =
    | 'matters.client'
    | 'experience-records.list-page.filters.case-group'
    | 'experience-records.list-page.filters.experience-record-group'
    | 'experience-records.list-page.filters.business-development-group';

type Props = {
    createXrNavigationUrl?: string;
    passiveFilter?: string;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
};

/** A row's experience record, when there is one and its case may be read: the cell links it under the same rule. */
export const getExperienceRecordHref = ({ matter, experienceRecord }: MatterExperienceRecordDto) =>
    matter.isReadable && experienceRecord ? `/experience-records/${experienceRecord.id}` : undefined;

const ExperienceRecordsTableWithFilters = ({
    createXrNavigationUrl,
    passiveFilter = '',
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
}: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();

    // Build default filters for experience records (not excluded from XR by default)
    const initialSearchParams = 'qf=matter.isExcludedFromXR==false';

    const [fetchData, { data, isFetching }] = useLazyGetExperienceRecordsWithFiltersQuery();
    const [updateMatterExperienceRecordExclusion] = useUpdateMatterExperienceRecordExclusionMutation();
    const [bulkUpdateMatterExperienceRecordExclusion] = useBulkUpdateMatterExperienceRecordExclusionMutation();
    const [selectedMatterIds, setSelectedMatterIds] = useState<number[]>([]);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['MATTER_STATE_TYPE', 'COUNTRY', 'BUSINESS_AREA_TYPE'],
    });
    const { data: hasSectorGroups, isLoading: isLoadingHasSectorGroups } = useGetHasSectorGroupsQuery();
    const isSubPracticeAreaEnabled = useIsFeatureEnabled(FeatureFlag.SUB_PRACTICE_AREA);
    const { data: currentTenant } = useGetCurrentTenantQuery();
    const baseCurrencyTypeCode = currentTenant?.baseCurrencyTypeCode ?? '';
    // Interval filters render only min/max labels, so the currency has to live on those
    const currencySuffix = baseCurrencyTypeCode ? t('common.currency-suffix', { currency: baseCurrencyTypeCode }) : '';
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    // With Rankings referees on, the record's referees are candidates, and its filters say so.
    const refereeCopy = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE) ? 'referee-candidate' : 'referee';

    const hasPermissionToExport = useHasPermissions(['EXPORT.EXPERIENCE_RECORDS']);
    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(
        useStartMatterExperienceRecordExportMutation,
        {
            entityType: 'MATTER_EXPERIENCE_RECORD',
            entityName: t('experience-records.experience-records'),
            fileName: EXPORT_CONFIG.MATTER_EXPERIENCE_RECORD.fileName,
        }
    );

    const columns = useMemo<DataTableColumnDef<MatterExperienceRecordDto>[]>(
        () => [
            {
                id: 'select',
                header: ({ table }) => {
                    const selectableMatters =
                        table
                            .getRowModel()
                            .rows.map((row) => row.original)
                            .filter((item) => !item.matter.isExcludedFromXR && item.matter.isEditable) || [];
                    const selectableMatterIds = selectableMatters.map((item) => item.matter.id);

                    return (
                        <Checkbox
                            variant="secondary"
                            indeterminate={
                                selectedMatterIds.length > 0 && selectedMatterIds.length < selectableMatterIds.length
                            }
                            checked={
                                selectableMatterIds.length > 0 &&
                                selectedMatterIds.length === selectableMatterIds.length
                            }
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setSelectedMatterIds(selectableMatterIds);
                                } else {
                                    setSelectedMatterIds([]);
                                }
                            }}
                        />
                    );
                },
                meta: {
                    // Checkbox / action columns: sized to their content rather than taking a share of
                    // the flex pool.
                    fixedPx: 64,
                },
                cell: ({ row }) => {
                    const { matter } = row.original;
                    const isSelected = selectedMatterIds.includes(matter.id);
                    const isDisabled = matter.isExcludedFromXR || !matter.isEditable;

                    return (
                        <Checkbox
                            variant="secondary"
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setSelectedMatterIds((prev) => [...prev, matter.id]);
                                } else {
                                    setSelectedMatterIds((prev) => prev.filter((id) => id !== matter.id));
                                }
                            }}
                        />
                    );
                },
                enableGrouping: false,
                enableSorting: false,
            },
            {
                id: 'matterLink',
                header: t('experience-records.list-page.table.case-client-status'),
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 200,
                },
                cell: ({ row }) => {
                    const { matter, experienceRecord } = row.original;
                    return matter.isReadable ? (
                        <div className="flex flex-col">
                            <RecordLink
                                withOrigin
                                className={`${ENTITY_LINK} font-semibold`}
                                to={`/matter/${matter.id}`}
                                label={matter.fullName ?? '-'}
                            />
                            {getLinks('contact', [
                                {
                                    id: matter.clientParty.id,
                                    name: partyNameString(matter.clientParty, {
                                        showPartyNumber: true,
                                        isPartyNumberUsageEnabled,
                                    }),
                                },
                            ])}
                            <div className="mt-1 flex items-center gap-1">
                                <MatterIndicators isConfidential={matter.isConfidential} variant="chip" />
                                <StatusChip
                                    type={getStatusChipType(matter.matterStateTypeCode)}
                                    label={getClassifierText(
                                        classifiers['MATTER_STATE_TYPE'],
                                        matter.matterStateTypeCode
                                    )}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-row gap-4">
                            <MatterIndicators isConfidential={matter.isConfidential} hasAccess={false} variant="chip" />
                            <div className="flex flex-col gap-1">
                                <p className="text-body1 font-semibold whitespace-nowrap">{matter.fullName ?? '-'}</p>
                                <p className="text-body1 font-semibold whitespace-nowrap">
                                    {experienceRecord?.name ?? '-'}
                                </p>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: 'matterEmployees',
                header: t('experience-records.list-page.table.assigned-to'),
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 200,
                },
                cell: ({ row }) => <AssignedEmployeesCell record={row.original} />,
            },
            {
                id: 'matterOffice',
                header: t('experience-records.list-page.table.opening-date-office'),
                accessorFn: (dto) => dto.matter.openedDate,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 200,
                },
                cell: ({ row }) => {
                    return <OpenedDateCell record={row.original} />;
                },
            },
            {
                id: 'matterExperienceRecord',
                header: t('experience-records.list-page.table.xr-name-description'),
                accessorFn: (dto) => dto.experienceRecord?.name,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 200,
                },
                cell: ({ row }) => {
                    const { experienceRecord, matter } = row.original;
                    if (!matter.isReadable) return null;

                    if (!experienceRecord) {
                        return (
                            <Button
                                {...buttonStyle({ variant: 'outlined', size: 'small' }, 'text-body2')}
                                disabled={matter.isExcludedFromXR}
                                onClick={() => navigateWithReturn(`/matter/${matter.id}/experience-records/new`)}
                            >
                                <FormattedMessage id="experience-records.list-page.add-new" />
                            </Button>
                        );
                    }

                    return (
                        <div className="flex flex-col gap-1">
                            <div className="flex gap-1">
                                {experienceRecord.status === 'DRAFT' && <XrDraftChip />}
                                {experienceRecord.isConfidential && (
                                    <XrConfidentialMarker label={t('experience-records.view.header.confidential')} />
                                )}
                                <Link
                                    className={`${ENTITY_LINK} font-semibold`}
                                    to={`/experience-records/${experienceRecord.id}`}
                                >
                                    {experienceRecord.name}
                                </Link>
                            </div>
                            {experienceRecord.description && (
                                <Tooltip content={experienceRecord.description}>
                                    <p className="text-body2 text-fg-secondary line-clamp-2">
                                        {experienceRecord.description}
                                    </p>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'actions',
                header: '',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    fixedPx: 92,
                },
                cell: ({ cell }) => {
                    const { matter } = cell.row.original;

                    const handleExcludeFromXR = (matterId: number) => {
                        updateMatterExperienceRecordExclusion({
                            id: matterId.toString(),
                            isExcludedFromXR: true,
                        });
                    };

                    return (
                        <ExperienceRecordActionButtons
                            matterId={matter.id}
                            isExcludedFromXR={matter.isExcludedFromXR}
                            isMatterEditable={matter.isEditable}
                            onExcludeFromXR={handleExcludeFromXR}
                        />
                    );
                },
            },
        ],
        [
            classifiers,
            isPartyNumberUsageEnabled,
            navigateWithReturn,
            updateMatterExperienceRecordExclusion,
            selectedMatterIds,
            setSelectedMatterIds,
        ]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: filterQuickFilters<FilterId>(
                [
                    {
                        id: 'isMyCaseQF',
                        selector: 'matterId',
                        comperator: '=ismycase=',
                        filters: [
                            {
                                tooltip: 'experience-records.list-page.quick-filters.my-cases-tooltip',
                                label: t('experience-records.list-page.quick-filters.my-cases'),
                                argument: 'true',
                            },
                        ],
                    },
                    {
                        id: 'hasExperienceRecordQF',
                        selector: 'hasExperienceRecord',
                        chipLabel: 'filters.show',
                        filters: [
                            {
                                tooltip: 'experience-records.list-page.quick-filters.cases-with-xr-tooltip',
                                label: t('experience-records.list-page.quick-filters.cases-with-xr'),
                                argument: 'true',
                            },
                            {
                                tooltip: 'experience-records.list-page.quick-filters.cases-without-xr-tooltip',
                                label: t('experience-records.list-page.quick-filters.cases-without-xr'),
                                argument: 'false',
                            },
                        ],
                    },
                    {
                        id: 'includedToXRQF',
                        selector: 'matter.isExcludedFromXR',
                        comperator: '==',
                        multiple: false,
                        falseIsYes: true,
                        label: 'experience-records.list-page.quick-filters.included-to-xr',
                        filters: [
                            {
                                tooltip: 'experience-records.list-page.quick-filters.included-to-xr-tooltip',
                                label: t('yes'),
                                argument: 'false',
                            },
                            {
                                tooltip: 'experience-records.list-page.quick-filters.excluded-from-xr-tooltip',
                                label: t('no'),
                                argument: 'true',
                            },
                        ],
                    },
                    {
                        // The view's draft flag is false on a case with no record, so those rows stay.
                        id: 'hideDraftsQF',
                        selector: 'experienceRecordIsDraft',
                        comperator: '==',
                        filters: [
                            {
                                tooltip: 'experience-records.list-page.quick-filters.hide-drafts-tooltip',
                                label: t('experience-records.list-page.quick-filters.hide-drafts'),
                                argument: 'false',
                            },
                        ],
                    },
                ],
                disabledFilterIds
            ),
            filters: enrichDynamicFiltersWithPassiveFilter(
                filterMainFilters<FilterId, FilterGroup>(
                    [
                        // Client filters
                        {
                            label: t('matters.filter.contact-name'),
                            selector: 'matterClientParty.id',
                            type: 'dynamicmultiselect',
                            subType: 'contact',
                            group: 'matters.client',
                            tooltip: 'matters.tooltips.list-displayed-for-relationship-type-selections',
                            paginated: {
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                searchField: ['name', 'partyNumber'],
                                filters: `relationshipTypeCode=in=(${[RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD].join(',')})`,
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('matters.filter.group-name'),
                            selector: 'matterClientParty.partyGroupParties.partyGroup.id',
                            type: 'dynamicmultiselect',
                            group: 'matters.client',
                            tooltip: 'matters.tooltips.list-displayed-for-relationship-type-selections',
                            paginated: {
                                fetch: useLazyGetMinGroupsListWithFiltersQuery,
                                searchField: 'groupName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'groupName',
                            },
                        },
                        {
                            label: t('contacts.about.relationship-partner'),
                            selector: 'matterClientParty.partners.employee.id',
                            type: 'dynamicmultiselect',
                            group: 'matters.client',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('contacts.about.relationship-manager'),
                            selector: 'matterClientParty.managers.employee.id',
                            type: 'dynamicmultiselect',
                            group: 'matters.client',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('matters.filter.contact-business-area'),
                            selector: 'matterClientParty.businessAreas.businessAreaCode',
                            type: 'multiselect',
                            group: 'matters.client',
                            options: classifiers
                                ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE'])
                                : [],
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'matters.client',
                            label: t('contacts.filter.sector-group'),
                            tooltip: 'experience-records.list-page.filters.tooltips.sector-group',
                            selector: 'matterClientParty.id',
                            comperator: '=hasclientbusinessareasinsectorgroup=',
                            getShouldHide: () => !hasSectorGroups,
                            paginated: {
                                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                                searchField: 'name',
                                filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('matters.filter.contact-country'),
                            selector: 'matterClientParty.addresses.countryCode',
                            type: 'multiselect',
                            group: 'matters.client',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                        },
                        // Case filters
                        {
                            selector: 'matterId',
                            type: 'checkboxgroup',
                            comperator: '=ismycase=',
                            group: 'experience-records.list-page.filters.case-group',
                            filters: [
                                {
                                    tooltip: 'experience-records.list-page.quick-filters.my-cases-tooltip',
                                    label: t('experience-records.list-page.quick-filters.my-cases'),
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            selector: 'hasExperienceRecord',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.case-group',
                            filters: [
                                {
                                    tooltip: 'experience-records.list-page.quick-filters.cases-with-xr-tooltip',
                                    label: t('experience-records.list-page.quick-filters.cases-with-xr'),
                                    argument: 'true',
                                },
                                {
                                    tooltip: 'experience-records.list-page.quick-filters.cases-without-xr-tooltip',
                                    label: t('experience-records.list-page.quick-filters.cases-without-xr'),
                                    argument: 'false',
                                },
                            ],
                        },
                        {
                            selector: 'matter.isExcludedFromXR',
                            type: 'checkboxgroup',
                            comperator: '==',
                            group: 'experience-records.list-page.filters.case-group',
                            filters: [
                                {
                                    tooltip: 'experience-records.list-page.quick-filters.included-to-xr-tooltip',
                                    label: t('experience-records.list-page.quick-filters.included-to-xr'),
                                    argument: 'false',
                                },
                                {
                                    tooltip: 'experience-records.list-page.quick-filters.excluded-from-xr-tooltip',
                                    label: t('experience-records.list-page.quick-filters.excluded-from-xr'),
                                    argument: 'true',
                                },
                            ],
                            multiple: false,
                            falseIsYes: true,
                        },
                        {
                            label: t('experience-records.list-page.filters.case-name'),
                            selector: 'matter.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            getOptionStartAdornment: renderMatterIndicators,
                            paginated: {
                                fetch: useLazyGetMattersListQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'fullName',
                            },
                        },
                        {
                            label: t('experience-records.list-page.filters.supervising-partner'),
                            selector: 'matter.supervisedByEmployee.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('experience-records.list-page.filters.responsible-lawyer'),
                            selector: 'matter.responsibleEmployee.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('matters.handled-by'),
                            selector: 'matter.employees.employee.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('experience-records.list-page.filters.handled-by-office'),
                            selector: 'matter.handledByOffice.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            paginated: {
                                fetch: useLazyGetMinOfficesWithFiltersQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                            getOptionEndAdornment: officeInactiveSuffix,
                        },
                        {
                            label: t('matters.filter.practice-areas'),
                            selector: 'matter.practiceAreas.practiceArea.id',
                            type: 'dynamicmultiselect',
                            group: 'experience-records.list-page.filters.case-group',
                            tooltip: 'matters.tooltips.filter-cases-practice-area',
                            paginated: {
                                fetch: useLazyGetPracticeAreasWithFiltersQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                                filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            label: t('matters.filter.sub-practice-areas'),
                            selector: 'matter.practiceAreas.practiceArea.id',
                            comperator: '=hassecondlevelpracticearea=',
                            group: 'experience-records.list-page.filters.case-group',
                            tooltip: 'matters.tooltips.filter-cases-sub-practice-area',
                            getOptionStartAdornment: (option) => option.taxonomyId && `[${option.taxonomyId}]`,
                            getShouldHide: () => !isSubPracticeAreaEnabled,
                            paginated: {
                                fetch: useLazyGetSecondLevelPracticeAreasUngroupedQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            label: t('experience-records.list-page.filters.case-status'),
                            selector: 'matter.matterStateTypeCode',
                            type: 'multiselect',
                            options: mapToFilterOptions(filterMatterStateTypes, classifiers['MATTER_STATE_TYPE']),
                            group: 'experience-records.list-page.filters.case-group',
                        },
                        {
                            label: t('matters.opening-date'),
                            selector: 'matter.openedDate',
                            type: 'daterange',
                            shouldDisplayQuickPick: true,
                            group: 'experience-records.list-page.filters.case-group',
                        },
                        {
                            label: t('matters.closing-date'),
                            selector: 'matter.closedDate',
                            type: 'daterange',
                            shouldDisplayQuickPick: true,
                            group: 'experience-records.list-page.filters.case-group',
                        },
                        {
                            label: t('matters.last-timecard-date'),
                            selector: 'matter.latestTimecardDate',
                            type: 'daterange',
                            tooltip: 'matters.tooltips.last-timecard-date',
                            group: 'experience-records.list-page.filters.case-group',
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'interval',
                            label: t('matters.filter.billable-hours'),
                            selector: 'id',
                            comperator: '=billablehoursrange=',
                            group: 'experience-records.list-page.filters.case-group',
                            minLabel: t('matters.filter.billable-hours-from'),
                            maxLabel: t('matters.filter.billable-hours-to'),
                        },
                        {
                            type: 'interval',
                            label: t('matters.filter.total-case-revenue'),
                            selector: 'id',
                            comperator: '=caserevenuerange=',
                            group: 'experience-records.list-page.filters.case-group',
                            minLabel: t('matters.filter.total-case-revenue-from') + currencySuffix,
                            maxLabel: t('matters.filter.total-case-revenue-to') + currencySuffix,
                        },
                        // Experience Record filters
                        {
                            label: t('experience-records.list-page.filters.xr-status'),
                            selector: 'experienceRecordStatus',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            filters: [
                                {
                                    label: t('experience-records.status.DRAFT'),
                                    argument: 'DRAFT',
                                    tooltip: 'experience-records.list-page.filters.xr-status-draft-tooltip',
                                },
                                {
                                    label: t('experience-records.status.FINAL'),
                                    argument: 'FINAL',
                                    tooltip: 'experience-records.list-page.filters.xr-status-final-tooltip',
                                },
                            ],
                        },
                        {
                            selector: 'experienceRecord.isConfidential',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.xr-is-confidential'),
                                    argument: 'true',
                                    tooltip: 'experience-records.list-page.filters.xr-is-confidential-tooltip',
                                },
                                {
                                    label: t('experience-records.list-page.filters.xr-is-not-confidential'),
                                    argument: 'false',
                                    tooltip: 'experience-records.list-page.filters.xr-is-not-confidential-tooltip',
                                },
                            ],
                        },
                        {
                            label: t('experience-records.list-page.filters.jurisdiction'),
                            selector: 'experienceRecord.countries.countryTypeCode',
                            type: 'multiselect',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                        },
                        {
                            type: 'interval',
                            label: t('experience-records.list-page.filters.deal-dispute-value'),
                            selector: 'experienceRecordValueAmount',
                            comperator: '=numerictextrange=',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            minLabel: t('experience-records.list-page.filters.deal-dispute-value-from'),
                            maxLabel: t('experience-records.list-page.filters.deal-dispute-value-to'),
                        },
                        {
                            label: t('experience-records.list-page.filters.client-consent-requested'),
                            selector: 'experienceRecord.clientConsentRequestedOn',
                            type: 'checkboxgroup',
                            comperator: '=notnull=',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.client-consent-requested'),
                                    argument: 'true',
                                    tooltip: 'experience-records.list-page.filters.client-consent-requested-tooltip',
                                },
                            ],
                        },
                        {
                            label: t('experience-records.list-page.filters.client-consent-received'),
                            selector: 'experienceRecord.clientConsentReceivedOn',
                            type: 'checkboxgroup',
                            comperator: '=notnull=',
                            group: 'experience-records.list-page.filters.experience-record-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.client-consent-received'),
                                    argument: 'true',
                                    tooltip: 'experience-records.list-page.filters.client-consent-received-tooltip',
                                },
                            ],
                        },
                        {
                            label: t('experience-records.list-page.filters.showcase-priority'),
                            selector: 'experienceRecord.isShowcasePriority',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.business-development-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.showcase-priority'),
                                    argument: 'true',
                                    tooltip: 'experience-records.list-page.filters.showcase-priority-tooltip',
                                },
                            ],
                        },
                        {
                            label: t('experience-records.list-page.filters.used-in-marketing'),
                            selector: 'experienceRecord.isUsedInMarketing',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.business-development-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.used-in-marketing'),
                                    argument: 'true',
                                    tooltip: 'experience-records.list-page.filters.used-in-marketing-tooltip',
                                },
                            ],
                        },
                        {
                            label: t('experience-records.list-page.filters.included-in-legal-directories'),
                            selector: 'experienceRecord.isIncludedInLegalDirectories',
                            type: 'checkboxgroup',
                            group: 'experience-records.list-page.filters.business-development-group',
                            filters: [
                                {
                                    label: t('experience-records.list-page.filters.included-in-legal-directories'),
                                    argument: 'true',
                                    tooltip:
                                        'experience-records.list-page.filters.included-in-legal-directories-tooltip',
                                },
                            ],
                        },
                        {
                            selector: 'experienceRecord.parties.id',
                            type: 'checkboxgroup',
                            comperator: '=gt=',
                            group: 'experience-records.list-page.filters.business-development-group',
                            filters: [
                                {
                                    label: t(`experience-records.list-page.filters.${refereeCopy}-assigned`),
                                    argument: '0',
                                    tooltip: `experience-records.list-page.filters.${refereeCopy}-assigned-tooltip`,
                                },
                            ],
                        },
                        {
                            label: t(`experience-records.list-page.filters.${refereeCopy}-name`),
                            selector: 'experienceRecord.parties.party.id',
                            type: 'dynamicmultiselect',
                            subType: 'contact',
                            group: 'experience-records.list-page.filters.business-development-group',
                            tooltip: `experience-records.list-page.filters.${refereeCopy}-name-tooltip`,
                            paginated: {
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                searchField: ['name', 'partyNumber'],
                                filters: `partyType==${PartyType.PERSON}`,
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                    ],
                    disabledGroups,
                    disabledFilterIds
                ),
                paginatedFieldPassiveFilters
            ),
            passiveFilter,
            searchBarSelectors: [
                { selector: 'matter.name' },
                { selector: 'matter.caseNumber' },
                { selector: 'matterClientParty.name' },
                { selector: 'experienceRecordName' },
                { selector: 'experienceRecordDescription' },
            ],
        }),
        [
            classifiers,
            disabledFilterIds,
            disabledGroups,
            isSubPracticeAreaEnabled,
            paginatedFieldPassiveFilters,
            passiveFilter,
            hasSectorGroups,
            currencySuffix,
            refereeCopy,
        ]
    );

    const handleBulkExcludeFromXR = useCallback(async () => {
        const updates = selectedMatterIds.map((matterId) => ({
            id: matterId,
            isExcludedFromXR: true,
        }));

        await bulkUpdateMatterExperienceRecordExclusion(updates);
        setSelectedMatterIds([]);
    }, [selectedMatterIds, bulkUpdateMatterExperienceRecordExclusion]);

    const filterLevelActionButtons = useCallback(() => {
        return (
            <div className="ml-4 flex flex-row justify-end gap-2">
                <Button
                    {...buttonStyle({ variant: 'outlined', size: 'small' })}
                    disabled={selectedMatterIds.length === 0}
                    onClick={() => {
                        void handleBulkExcludeFromXR();
                    }}
                >
                    <FormattedMessage
                        id="experience-records.list-page.actions.exclude-from-xr"
                        values={{ count: selectedMatterIds.length }}
                    />
                </Button>
            </div>
        );
    }, [selectedMatterIds, handleBulkExcludeFromXR]);

    if (isLoadingClassifiers || isLoadingHasSectorGroups) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<MatterExperienceRecordDto>
            getRowHref={getExperienceRecordHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="experience-records"
            columns={columns}
            data={data}
            isLoading={isFetching}
            singularTextId="experience-records.list-page.table.record-singular"
            pluralTextId="experience-records.list-page.table.record-plural"
            noEntriesId="experience-records.list-page.table.no-records-found"
            noEntriesBodyId="experience-records.empty.body"
            noEntriesIcon={StarOutlineOutlinedIcon}
            noEntriesAction={
                createXrNavigationUrl != null ? (
                    <ListCreateButton
                        permissions={['CREATE.EXPERIENCE_RECORDS.ALL']}
                        variant="contained"
                        color="secondary"
                        onClick={() => navigateWithReturn(createXrNavigationUrl)}
                        labelId="experience-records.list-page.add-new"
                        newLabelId="new-entity.experience-record"
                    />
                ) : null
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            extraHeaderComponent={
                createXrNavigationUrl != null && (
                    <ListCreateButton
                        permissions={['CREATE.EXPERIENCE_RECORDS.ALL']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(createXrNavigationUrl)}
                        labelId="experience-records.list-page.add-new"
                        newLabelId="new-entity.experience-record"
                    />
                )
            }
            renderSearchDescriptionLevelComponent={filterLevelActionButtons}
            initialSearchParams={initialSearchParams}
            exportHookResults={
                hasPermissionToExport
                    ? [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
                    : undefined
            }
        />
    );
};

export default ExperienceRecordsTableWithFilters;
